import {
  GeoPoint,
  Timestamp,
  doc,
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { firestore } from '@core/services/firebase/firebase-service'
import type {
  ActivityExplore,
  BelongsToUser,
  LatLngLocation,
  TranslationsMap,
} from '@domain/models/activity-explore'

const USERS_COLLECTION = 'users'
const MAIN_CATEGORIES_COLLECTION = 'main_categories'
const ORGANISATIONS_COLLECTION = 'organisations'

function getCollectionRef(
  collectionName: string,
  id: string,
): DocumentReference<DocumentData> {
  if (!firestore) {
    throw new Error('Firestore is not initialized.')
  }
  return doc(firestore, collectionName, id)
}

function isReferenceLike(value: unknown): value is DocumentReference<DocumentData> {
  return Boolean(value && typeof value === 'object' && 'id' in value)
}

function refToId(value: unknown): string | undefined {
  if (!isReferenceLike(value)) {
    return undefined
  }
  return value.id
}

function idsToRefs(
  ids: string[] | undefined,
  collectionName: string,
): DocumentReference<DocumentData>[] | undefined {
  if (ids === undefined) {
    return undefined
  }
  return ids
    .map((id) => id.trim())
    .filter((id) => id.length > 0)
    .map((id) => getCollectionRef(collectionName, id))
}

function refsToIds(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined
  }
  return value
    .map((item) => refToId(item))
    .filter((item): item is string => Boolean(item))
}

function parseLatLng(value: unknown): LatLngLocation | null | undefined {
  if (value === null) {
    return null
  }
  if (value instanceof GeoPoint) {
    return {
      latitude: value.latitude,
      longitude: value.longitude,
    }
  }
  if (
    value &&
    typeof value === 'object' &&
    'latitude' in value &&
    'longitude' in value &&
    typeof value.latitude === 'number' &&
    typeof value.longitude === 'number'
  ) {
    return {
      latitude: value.latitude,
      longitude: value.longitude,
    }
  }
  return undefined
}

function toGeoPoint(value: LatLngLocation | null | undefined): GeoPoint | null | undefined {
  if (value === undefined) {
    return undefined
  }
  if (value === null) {
    return null
  }
  return new GeoPoint(value.latitude, value.longitude)
}

function toOptionalDate(value: unknown): Date | undefined {
  if (value instanceof Timestamp) {
    return value.toDate()
  }
  if (value instanceof Date) {
    return value
  }
  return undefined
}

function toTranslationsMap(value: unknown): TranslationsMap | undefined {
  if (!value || typeof value !== 'object') {
    return undefined
  }
  const map = value as Record<string, unknown>
  return {
    spanish: typeof map['spanish'] === 'string' ? map['spanish'] : undefined,
    german: typeof map['german'] === 'string' ? map['german'] : undefined,
    french: typeof map['french'] === 'string' ? map['french'] : undefined,
    portugese: typeof map['portugese'] === 'string' ? map['portugese'] : undefined,
  }
}

function toBelongsToUser(value: unknown): BelongsToUser | undefined {
  if (!value || typeof value !== 'object') {
    return undefined
  }
  const map = value as Record<string, unknown>
  return {
    name: typeof map['name'] === 'string' ? map['name'] : undefined,
    profilePhoto:
      typeof map['profile_photo'] === 'string'
        ? map['profile_photo']
        : undefined,
    userRefId: refToId(map['user_ref']),
    location: parseLatLng(map['location']),
  }
}

export function stripNullishDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    const cleaned = value
      .map((item) => stripNullishDeep(item))
      .filter((item) => item !== null && item !== undefined)
    return cleaned as T
  }

  if (!value || typeof value !== 'object') {
    return value
  }

  if (value instanceof Date || value instanceof Timestamp || value instanceof GeoPoint) {
    return value
  }

  if (isReferenceLike(value)) {
    return value
  }

  const output: Record<string, unknown> = {}
  for (const [key, nestedValue] of Object.entries(value as Record<string, unknown>)) {
    const cleaned = stripNullishDeep(nestedValue)
    if (cleaned !== null && cleaned !== undefined) {
      output[key] = cleaned
    }
  }

  return output as T
}

export function fromFirestoreActivity(
  docSnap: DocumentSnapshot<DocumentData> | QueryDocumentSnapshot<DocumentData>,
): ActivityExplore {
  const data = (docSnap.data() ?? {}) as Record<string, unknown>

  return {
    uid: docSnap.id,
    title: typeof data['title'] === 'string' ? data['title'] : undefined,
    newsCategoryId:
      typeof data['news_category_id'] === 'string'
        ? data['news_category_id']
        : refToId(data['news_category_ref']),
    newsCategoryString:
      typeof data['news_category_string'] === 'string'
        ? data['news_category_string']
        : typeof data['main_category_string'] === 'string'
          ? data['main_category_string']
          : undefined,
    mainCategoryId: refToId(data['main_category_ref']),
    mainCategoryString:
      typeof data['main_category_string'] === 'string'
        ? data['main_category_string']
        : undefined,
    image: typeof data['image'] === 'string' ? data['image'] : undefined,
    imageThumbnailUrl:
      typeof data['image_thumbnail_url'] === 'string'
        ? data['image_thumbnail_url']
        : typeof data['imageThumbnailUrl'] === 'string'
          ? data['imageThumbnailUrl']
          : undefined,
    imageMediumUrl:
      typeof data['image_medium_url'] === 'string'
        ? data['image_medium_url']
        : typeof data['imageMediumUrl'] === 'string'
          ? data['imageMediumUrl']
          : undefined,
    imageLargeUrl:
      typeof data['image_large_url'] === 'string'
        ? data['image_large_url']
        : typeof data['imageLargeUrl'] === 'string'
          ? data['imageLargeUrl']
          : undefined,
    imageWebpUrl:
      typeof data['image_webp_url'] === 'string'
        ? data['image_webp_url']
        : typeof data['imageWebpUrl'] === 'string'
          ? data['imageWebpUrl']
          : undefined,
    publishTime: toOptionalDate(data['publish_time']),
    article: typeof data['article'] === 'string' ? data['article'] : undefined,
    articleMarkdown:
      typeof data['article_markdown'] === 'string'
        ? data['article_markdown']
        : typeof data['articleMarkdown'] === 'string'
          ? data['articleMarkdown']
          : undefined,
    sourceUrl:
      typeof data['source_url'] === 'string' ? data['source_url'] : undefined,
    containsYoutubeVideo:
      typeof data['contains_youtube_video'] === 'boolean'
        ? data['contains_youtube_video']
        : undefined,
    youtubeLink:
      typeof data['youtube_link'] === 'string' ? data['youtube_link'] : undefined,
    likeCount:
      typeof data['like_count'] === 'number' ? data['like_count'] : undefined,
    commentsCount:
      typeof data['comments_count'] === 'number'
        ? data['comments_count']
        : undefined,
    importanceScore:
      typeof data['importance_score'] === 'number'
        ? data['importance_score']
        : undefined,
    videoUrl:
      typeof data['video_url'] === 'string' ? data['video_url'] : undefined,
    usersLiked: refsToIds(data['users_liked']),
    filters: Array.isArray(data['filters'])
      ? data['filters'].filter((item): item is string => typeof item === 'string')
      : undefined,
    readBy: refsToIds(data['read_by']),
    belongsToUser: toBelongsToUser(data['belongs_to_user']),
    translations: toTranslationsMap(data['translations']),
    titleTranslations: toTranslationsMap(data['titleTranslations']),
    isTranslated:
      typeof data['is_translated'] === 'boolean'
        ? data['is_translated']
        : undefined,
    orgRefId: refToId(data['org_ref']),
    promoted:
      typeof data['promoted'] === 'boolean' ? data['promoted'] : undefined,
    recommended:
      typeof data['recommended'] === 'boolean' ? data['recommended'] : undefined,
    favoritedBy: refsToIds(data['favorited_by']),
    dynamicLink:
      typeof data['dynamic_link'] === 'string' ? data['dynamic_link'] : undefined,
  }
}

export function toFirestoreActivityPatch(
  input: Partial<ActivityExplore>,
): Record<string, unknown> {
  const belongsToUser = input.belongsToUser
    ? {
        name: input.belongsToUser.name,
        profile_photo: input.belongsToUser.profilePhoto,
        user_ref: input.belongsToUser.userRefId
          ? getCollectionRef(USERS_COLLECTION, input.belongsToUser.userRefId)
          : undefined,
        location: toGeoPoint(input.belongsToUser.location),
      }
    : undefined

  const payload: Record<string, unknown> = {
    title: input.title,
    news_category_id: input.newsCategoryId,
    news_category_string: input.newsCategoryString,
    main_category_ref: input.mainCategoryId
      ? getCollectionRef(MAIN_CATEGORIES_COLLECTION, input.mainCategoryId)
      : undefined,
    main_category_string: input.mainCategoryString,
    image: input.image,
    image_thumbnail_url: input.imageThumbnailUrl,
    image_medium_url: input.imageMediumUrl,
    image_large_url: input.imageLargeUrl,
    image_webp_url: input.imageWebpUrl,
    publish_time: input.publishTime
      ? Timestamp.fromDate(input.publishTime)
      : undefined,
    article: input.article,
    article_markdown: input.articleMarkdown,
    source_url: input.sourceUrl,
    contains_youtube_video: input.containsYoutubeVideo,
    youtube_link: input.youtubeLink,
    like_count: input.likeCount,
    comments_count: input.commentsCount,
    importance_score: input.importanceScore,
    video_url: input.videoUrl,
    users_liked: idsToRefs(input.usersLiked, USERS_COLLECTION),
    filters: input.filters,
    read_by: idsToRefs(input.readBy, USERS_COLLECTION),
    belongs_to_user: belongsToUser,
    translations: input.translations,
    titleTranslations: input.titleTranslations,
    is_translated: input.isTranslated,
    org_ref: input.orgRefId
      ? getCollectionRef(ORGANISATIONS_COLLECTION, input.orgRefId)
      : undefined,
    promoted: input.promoted,
    recommended: input.recommended,
    favorited_by: idsToRefs(input.favoritedBy, USERS_COLLECTION),
    dynamic_link: input.dynamicLink,
  }

  return stripNullishDeep(payload)
}
